import { useSelector } from 'react-redux';
import TableContainer from '@mui/material/TableContainer';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import { styled } from '@mui/material/styles';
import { Table } from '@components/Table';
import Button from '@components/Button';
import ActionIconButton from '@components/ActionIconButton';
import { Typography } from '@components/Typography';
import Avatar from '@components/Avatar';
import Tooltip from '@components/Tooltip';
import { useTranslation, useTableSort } from '@hooks';
import { formatDateOrEmpty, toTimestamp } from '@helpers/dates';
import { getFullName, getInitialsFull } from '@helpers/userName';
import { FORM_TYPE } from '../TeamEditModal';

const ActionsWrapper = styled('div')({
    display: 'flex',
    gap: 16,
});

const MembersWrapper = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    width: '100%',
});

const memberLabel = member => getFullName(member.firstName, member.lastName) || member.email;

const GetColumnNames = () => {
    const { t } = useTranslation();
    return [
        {
            key: 0,
            sortKey: 'name',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('SettingsTeams.teamName')}
                </Typography>
            ),
        },
        {
            key: 1,
            sortKey: 'permissions',
            align: 'left',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('SettingsTeams.permissions')}
                </Typography>
            ),
        },
        {
            key: 2,
            sortKey: 'members',
            align: 'left',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('SettingsTeams.members')}
                </Typography>
            ),
        },
        {
            key: 3,
            sortKey: 'createdAt',
            align: 'left',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.createdOn')}
                </Typography>
            ),
        },
        { key: 4, component: <Typography align="left" variant="subtitle2"></Typography> },
    ];
};

const getEnabledPermissionsCount = team => {
    const permissions = team.permissions || [];
    return permissions.filter(p => !p.endsWith(':none')).length;
};

const SORT_COLUMNS = {
    name: 'name',
    permissions: getEnabledPermissionsCount,
    members: team => (team.members || []).length,
    createdAt: team => toTimestamp(team.createdAt),
};

const renderMembers = (team, t) => {
    const members = team.members || [];
    if (members.length === 0) {
        return (
            <Typography variant="body2" align="left" sx={{ color: 'var(--text-secondary)' }}>
                {t('common.none')}
            </Typography>
        );
    }

    return (
        <MembersWrapper>
            {members.map(member => {
                const name = memberLabel(member);
                return (
                    <Tooltip key={member.userId} title={name} placement="top">
                        <span>
                            <Avatar
                                size="small"
                                fullName={name}
                                label={getInitialsFull(name)}
                                image={member.profileImageUrl}
                            />
                        </span>
                    </Tooltip>
                );
            })}
        </MembersWrapper>
    );
};

const getRowsData = (teams, t, language, onEdit, onDuplicate, onDelete) => {
    return teams.map(team => [
        {
            component: (
                <Typography variant="subtitle2" align="left">
                    {team.name}
                </Typography>
            ),
        },
        {
            component: (
                <Typography variant="body2" align="left">
                    {t('SettingsTeams.enabledPermissionsCount', { count: getEnabledPermissionsCount(team) })}
                </Typography>
            ),
        },
        {
            component: renderMembers(team, t),
        },
        {
            component: (
                <Typography variant="body2" align="left">
                    {formatDateOrEmpty(team.createdAt, 'd MMM yyyy', language)}
                </Typography>
            ),
        },
        {
            value: team,
            component: (
                <ActionsWrapper>
                    <ActionIconButton title={t('common.edit')} onClick={() => onEdit(team)}>
                        <EditIcon fontSize="inherit" />
                    </ActionIconButton>
                    <ActionIconButton title={t('common.duplicate')} onClick={() => onDuplicate(team)}>
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                    </ActionIconButton>
                    <ActionIconButton title={t('common.delete')} onClick={() => onDelete(team)}>
                        <DeleteIcon fontSize="inherit" />
                    </ActionIconButton>
                </ActionsWrapper>
            ),
        },
    ]);
};

const TeamsTable = ({ teams, isLoading, setSelectedTeam, setDeleteModalOpen, setModal, onDuplicateTeam }) => {
    const { t } = useTranslation();
    const language = useSelector(state => state.auth.user?.language);
    const { sort, onSortChange, sortedData } = useTableSort(teams, { columns: SORT_COLUMNS });

    const onEdit = team => {
        setSelectedTeam(team);
        setModal(prev => ({ ...prev, open: true, type: FORM_TYPE.EDIT }));
    };

    const onDuplicate = team => {
        onDuplicateTeam(team);
    };

    const onDelete = team => {
        setSelectedTeam(team);
        setDeleteModalOpen(true);
    };

    return (
        <>
            <TableContainer>
                <Table
                    data={sortedData}
                    columnNames={GetColumnNames()}
                    rows={getRowsData(sortedData, t, language, onEdit, onDuplicate, onDelete)}
                    onRowClick={team => onEdit(team)}
                    isDataLoading={isLoading}
                    isInteractive
                    isCustomActionComponent
                    sort={sort}
                    onSortChange={onSortChange}
                />
            </TableContainer>
            <div style={{ padding: '24px 0' }}>
                <Button
                    type="button"
                    variant="contained"
                    onClick={() => {
                        setSelectedTeam(null);
                        setModal(prev => ({ ...prev, open: true, type: FORM_TYPE.NEW }));
                    }}
                >
                    {t('SettingsTeams.createTeam')}
                </Button>
            </div>
        </>
    );
};

export default TeamsTable;
